import { ApiResponse } from 'apisauce';
import { extractOrderFromValues } from '../../../components/Crud/useFilter.utils';
import { paginate } from '../../../lib/nest';
import { PaginatedResponse } from '../../../types/request.types';
import { UseInfiniteDataQueryFnContext } from '../useInfiniteDataQuery.types';

export class InfiniteDataQueryFnUtils {
  static fetcherWithCount<Data = unknown>(fetcher: PaginatedApiFetcher<Data>) {
    return this.fetcherToDataWithCount(fetcher);
  }

  static fetcherWithCountAndParams<Data = unknown>(
    fetcher: PaginatedApiFetcher<Data>,
    params?: Record<string, any>,
  ) {
    return (context: UseInfiniteDataQueryFnContext) =>
      this.fetcherToDataWithCount(fetcher)({ ...context, params });
  }

  static fetcherWithoutCount<Data = unknown>(
    fetcher: ApiFetcherToReturnData<Data>,
    fetcherToGetCount: ApiFetcherToReturnCount,
  ) {
    return async (params: DefaultFetcherParams) => {
      const dataPromise = this.defaultFetcher<Data[], ApiFetcherToReturnData<Data>>(fetcher)(
        params,
      );

      const [data, count] = await Promise.all([
        dataPromise,
        params.skip > 0
          ? Promise.resolve(0)
          : this.defaultFetcher<number, ApiFetcherToReturnCount>(fetcherToGetCount)(params),
      ]);

      return {
        data,
        countData: {
          total: count || 0,
        },
      };
    };
  }

  static fetcherPageOnly<Data = unknown>(fetcher: ApiFetcherToReturnData<Data>) {
    return async (params: DefaultFetcherParams) => ({
      data: await this.defaultFetcher<Data[], ApiFetcherToReturnData<Data>>(fetcher)(params),
    });
  }

  static defaultFetcher<Data, Fetcher extends GenericApiFetcher<Data>>(fetcher: Fetcher) {
    return async ({ limit, skip, params, signal, order: defaultOrderBy }: DefaultFetcherParams) => {
      const orderFromParams = extractOrderFromValues(params);

      const { order: _omitOrder, ...restParams } = params ?? {};

      const res = await fetcher(
        {
          skip,
          limit,
          ...paginate.order(orderFromParams || defaultOrderBy),
          ...restParams,
        },
        signal,
      );

      if (!res.ok) {
        throw res.originalError;
      }

      return res.data as Data;
    };
  }

  private static fetcherToDataWithCount<Data = unknown>(fetcher: PaginatedApiFetcher<Data>) {
    return async (params: DefaultFetcherParams) => {
      const data = await this.defaultFetcher<PaginatedResponse<Data>, PaginatedApiFetcher<Data>>(
        fetcher,
      )(params);

      return {
        data: data.data,
        countData: {
          total: data.count || 0,
        },
      };
    };
  }
}

type GenericApiFetcher<Data = unknown> = (
  params?: any,
  signal?: AbortSignal,
) => Promise<ApiResponse<Data>>;

export type PaginatedApiFetcher<Data = unknown> = GenericApiFetcher<PaginatedResponse<Data>>;

type ApiFetcherToReturnData<Data = unknown> = GenericApiFetcher<Data[]>;

type ApiFetcherToReturnCount = GenericApiFetcher<number>;

interface DefaultFetcherParams extends UseInfiniteDataQueryFnContext {
  params?: Record<string, unknown>;
}
